import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaEdit,
  FaCalendarAlt,
  FaClock,
  FaGlobe,
  FaUser,
  FaFolder,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { apiFetch } from '../../../utils/api';

export default function BlogPreviewPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiFetch(`/api/blogs/slug/${slug}`);
        if (!res.ok) {
          throw new Error('Failed to fetch blog');
        }
        const data = await res.json();
        setBlog(data);
      } catch (err) {
        console.error(err);
        setError('Blog post could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-12">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
          <p className="text-sm font-medium text-slate-500">Loading blog preview...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <p className="text-base font-semibold text-red-600">{error || 'Blog not found.'}</p>
        <Link
          to="/admin/blogs"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <FaArrowLeft /> Back to Blogs
        </Link>
      </div>
    );
  }

  const isPublished = blog.status === 'Published';

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Top Action Bar */}
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/blogs"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-white hover:text-slate-900 hover:shadow-sm"
          >
            <FaArrowLeft className="text-xs" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  isPublished
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border border-amber-200 bg-amber-50 text-amber-700'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                {blog.status} Preview
              </span>
              <span className="text-xs text-slate-400">/blogs/{blog.slug}</span>
            </div>
            <h1 className="mt-0.5 text-lg font-bold text-slate-900 line-clamp-1">{blog.title}</h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isPublished && (
            <a
              href={`/blogs/${blog.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <FaExternalLinkAlt className="text-[10px]" /> Open Public Page
            </a>
          )}
          <Link
            to={`/admin/blogs/edit/${blog._id}`}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
          >
            <FaEdit /> Edit This Post
          </Link>
        </div>
      </div>

      {/* Main Preview Article Card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Featured Hero Image */}
        {blog.featuredImage && (
          <div className="relative h-72 w-full bg-slate-100 sm:h-96">
            <img src={blog.featuredImage} alt={blog.title} className="h-full w-full object-cover" />
          </div>
        )}

        <div className="p-6 sm:p-10 space-y-6">
          {/* Taxonomy & Metadata */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
            {blog.category && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 font-semibold text-amber-800">
                <FaFolder className="text-[10px]" /> {blog.category}
              </span>
            )}
            <span className="flex items-center gap-1">
              <FaUser className="text-[10px] text-slate-400" /> By {blog.authorName || 'Admin'}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <FaCalendarAlt className="text-[10px] text-slate-400" />{' '}
              {new Date(blog.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <FaClock className="text-[10px] text-slate-400" /> {blog.readingTime || 1} min read
            </span>
          </div>

          {/* Title & Short Description */}
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl leading-tight">{blog.title}</h2>
            {blog.shortDescription && (
              <p className="mt-3 text-base text-slate-600 italic border-l-4 border-amber-400 pl-4 py-1">
                {blog.shortDescription}
              </p>
            )}
          </div>

          {/* Tag Pills */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-2">
              {blog.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Separator */}
          <hr className="border-slate-200" />

          {/* Formatted Content */}
          <div
            className="blog-content prose prose-slate max-w-none text-[15px] leading-relaxed text-slate-800 prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-blue-600 prose-img:rounded-xl prose-p:my-2 prose-headings:mt-4 prose-headings:mb-1.5"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* SEO Metadata Drawer */}
          <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm mb-3">
              <FaGlobe className="text-amber-500" />
              <span>SEO Meta Configurations</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-xs">
              <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Meta Title</p>
                <p className="mt-1 font-medium text-slate-800">{blog.metaTitle || 'Default'}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Meta Description</p>
                <p className="mt-1 font-medium text-slate-800">{blog.metaDescription || 'Default'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
